import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Spinner, TableSortingState } from '@ibsys/kit';
import {
  RenessansOperatorsSortBy,
  useRenessansDialogResults,
  useRenessansOperators,
  useRenessansSummary,
} from '@/entities/renessans-dashboards';
import { useTableState } from '@/entities/settings';
import { PageContainer } from '@/shared/ui';
import { RenessansFilters } from './RenessansFilters';
import { KpiRow } from './KpiRow';
import { CallsChart } from './CallsChart';
import { AvgDurationChart } from './AvgDurationChart';
import { OperatorsTable } from './OperatorsTable';
import { DialogResultsWidget } from './DialogResultsWidget';
import { ComparisonMode, ComparisonWidget } from './ComparisonWidget';

const OPERATORS_PAGE_SIZE = 50;

/** Подстрока имени группы для режима «Группы» в сравнении длительности — зашита на фронте. */
const GROUP_NAME_FILTER = 'ренессанс';

/**
 * Период по умолчанию — фиксированные даты демонстрационного дашборда:
 * зашиты намеренно, от текущей даты не вычисляются.
 */
const DEFAULT_DATE_FROM = '2026-03-01';
const DEFAULT_DATE_TO = '2026-04-01';
/** Сортировка таблицы операторов по умолчанию: убывание по количеству звонков. */
const DEFAULT_OPERATORS_SORTING: TableSortingState = [{ id: 'calls_count', desc: true }];

/**
 * Страница «Дашборд КЦ»: KPI и тренды периода, таблица операторов с серверной
 * пагинацией и сортировкой, распределение диалогов по результату и сравнение
 * длительности по операторам или по группам.
 *
 * Все фильтры — локальное состояние страницы; распределение по результату
 * своего фильтра не имеет и считается по всему срезу периода.
 */
export const RenessansDashboardPage: FC = () => {
  const { t } = useTranslation('renessansDashboard');

  const [dateFrom, setDateFrom] = useState(DEFAULT_DATE_FROM);
  const [dateTo, setDateTo] = useState(DEFAULT_DATE_TO);
  const [comparisonMode, setComparisonMode] = useState<ComparisonMode>('operator');
  const [operatorsPage, setOperatorsPage] = useState(1);

  // Состояние таблицы операторов (сортировка и ширины колонок) персистится в настройках
  // пользователя по ключу `renessansOperatorsTable` через useTableState (драйвер localStorage).
  const { settings: operatorsTableSettings, actions: operatorsTableActions } = useTableState(
    'renessansOperatorsTable',
    'localStorage',
    { sorting: DEFAULT_OPERATORS_SORTING },
  );

  const currentSort = operatorsTableSettings.sorting[0];
  const operatorsSortBy = (currentSort?.id ?? 'calls_count') as RenessansOperatorsSortBy;
  const operatorsSortDir: 'asc' | 'desc' = currentSort?.desc === false ? 'asc' : 'desc';

  const periodParams = useMemo(() => ({ date_from: dateFrom, date_to: dateTo }), [dateFrom, dateTo]);

  const summaryQuery = useRenessansSummary(periodParams);

  const operatorsTableQuery = useRenessansOperators({
    ...periodParams,
    sort_by: operatorsSortBy,
    sort_dir: operatorsSortDir,
    limit: OPERATORS_PAGE_SIZE,
    offset: (operatorsPage - 1) * OPERATORS_PAGE_SIZE,
  });

  const operatorsChartQuery = useRenessansOperators({
    ...periodParams,
    sort_by: 'avg_duration',
    sort_dir: 'desc',
    limit: 0,
    group_by: comparisonMode === 'group' ? 'employee_group' : undefined,
    group_name_contains: comparisonMode === 'group' ? GROUP_NAME_FILTER : undefined,
  });

  const dialogResultsQuery = useRenessansDialogResults(periodParams);

  const handleResetFilters = useCallback(() => {
    setDateFrom(DEFAULT_DATE_FROM);
    setDateTo(DEFAULT_DATE_TO);
    setComparisonMode('operator');
    setOperatorsPage(1);
    operatorsTableActions.onSortingChange(DEFAULT_OPERATORS_SORTING);
  }, [operatorsTableActions]);

  const handleChangePagination = useCallback((nextPage: number) => {
    setOperatorsPage(nextPage);
  }, []);

  const handleChangeSorting = useCallback(
    (nextSortBy: RenessansOperatorsSortBy, nextSortDir: 'asc' | 'desc') => {
      operatorsTableActions.onSortingChange([{ id: nextSortBy, desc: nextSortDir === 'desc' }]);
      setOperatorsPage(1);
    },
    [operatorsTableActions],
  );

  const summary = summaryQuery.data;

  return (
    <PageContainer limitedWidth>
      <div className="h-full w-full pb-4 flex flex-col gap-4">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold">{t('title')}</h1>
          {summaryQuery.isFetching && !summaryQuery.isLoading && <Spinner className="size-4" />}
        </div>

        <RenessansFilters
          dateFrom={dateFrom}
          dateTo={dateTo}
          onChangePeriod={(from, to) => {
            setDateFrom(from);
            setDateTo(to);
          }}
          onReset={handleResetFilters}
        />

        <KpiRow
          totalCalls={summary?.totalCalls ?? 0}
          avgDuration={summary?.avgDuration ?? null}
          totalDuration={summary?.totalDuration ?? 0}
        />

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <CallsChart items={summary?.callsTrend ?? []} />
          <AvgDurationChart items={summary?.avgDurationTrend ?? []} />
        </div>

        <OperatorsTable
          data={operatorsTableQuery.data}
          isLoading={operatorsTableQuery.isFetching}
          page={operatorsPage}
          perPage={OPERATORS_PAGE_SIZE}
          sortBy={operatorsSortBy}
          sortDir={operatorsSortDir}
          columnSizing={operatorsTableSettings.columnSizing}
          onChangePagination={handleChangePagination}
          onChangeSorting={handleChangeSorting}
          onColumnSizingChange={operatorsTableActions.setColumnSizing}
        />

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <DialogResultsWidget data={dialogResultsQuery.data} isLoading={dialogResultsQuery.isFetching} />
          <ComparisonWidget
            data={operatorsChartQuery.data}
            isLoading={operatorsChartQuery.isFetching}
            mode={comparisonMode}
            onChangeMode={setComparisonMode}
          />
        </div>
      </div>
    </PageContainer>
  );
};
